/**
 * Firebase Authentication Service
 * 
 * Handles all authentication operations including Google OAuth and email/password
 */

import { 
  signInWithPopup, 
  signInWithEmailAndPassword, 
  createUserWithEmailAndPassword,
  signOut, 
  onAuthStateChanged,
  updateProfile,
  type User as FirebaseUser
} from 'firebase/auth';
import { auth, googleProvider } from './firebaseConfig';
import type { User } from '../models/types';

export interface AuthState {
  user: User | null;
  loading: boolean;
  error: string | null;
}

export type UserRole = 'superadmin' | 'admin' | 'api_customer' | 'customer';
export type AppName = 'offshore-sails' | 'madebb';

export interface UserWithRole extends User {
  role: UserRole;
  domain: string;
}

class AuthService {
  private authStateListeners: ((user: UserWithRole | null) => void)[] = [];

  // Role-based access control configuration
  private readonly superAdminEmail = 'skip@precisionsailscaribbean.com';
  private readonly adminDomain = 'precisionsailscaribbean.com';
  private readonly apiCustomerDomains = ['precisionsailloft.com', 'doylecaribbean.com'];

  // Legacy admin emails (for backward compatibility)
  private readonly legacyAdminEmails = [
    'skip@precisionsailscaribbean.com',
    'skipsavoie@gmail.com'
  ];

  // Domain authorization configuration per application
  private readonly authorizedDomains = {
    'offshore-sails': [
      'precisionsailloft.com',
      'precisionsailscaribbean.com',
      'made.bb',
      'doylecaribbean.com'
    ],
    'madebb': [
      'made.bb',
      'precisionsailscaribbean.com',
      'doylecaribbean.com'
    ]
  };

  // Authorized email addresses for Made.bb (in addition to domains)
  private readonly madebbAuthorizedEmails = [
    'msavoie@gmail.com',
    'skipsavoie@gmail.com'
  ];

  // OS-INV-04: Authorized email addresses for Offshore Sails (in addition to
  // domains). Mirrors madebbAuthorizedEmails so superadmin/override accounts can
  // sign in to OS even without an offshore-sails domain. Per-tenant access is
  // still enforced downstream by the Firebase `tenants` claim + backend
  // requireTenant('offshore'); this only gates the login step.
  private readonly offshoreAuthorizedEmails = [
    'msavoie@gmail.com',
    'skipsavoie@gmail.com'
  ];

  constructor() {
    // Listen for authentication state changes
    onAuthStateChanged(auth, (firebaseUser) => {
      const user = firebaseUser ? this.convertFirebaseUser(firebaseUser) : null;
      this.notifyListeners(user);
    });
  }

  /**
   * Determine user role based on email address
   */
  private determineUserRole(email: string): UserRole {
    const normalizedEmail = email.toLowerCase();
    const domain = this.extractDomain(normalizedEmail);

    // Check for superadmin
    if (normalizedEmail === this.superAdminEmail) {
      return 'superadmin';
    }

    // Check legacy admin emails (maintain backward compatibility)
    if (this.legacyAdminEmails.includes(normalizedEmail)) {
      return 'superadmin'; // Treat legacy admins as superadmin for now
    }

    // Check for admin domain
    if (domain === this.adminDomain) {
      return 'admin';
    }

    // Check for API customer domains
    if (this.apiCustomerDomains.includes(domain)) {
      return 'api_customer';
    }

    // Default to customer
    return 'customer';
  }

  /**
   * Extract domain from email address
   */
  private extractDomain(email: string): string {
    const parts = email.split('@');
    return parts.length === 2 ? parts[1] : '';
  }

  /**
   * Check if email address has admin privileges (legacy method)
   */
  private isAdminEmail(email: string): boolean {
    const role = this.determineUserRole(email);
    return role === 'superadmin' || role === 'admin';
  }

  /**
   * Convert Firebase User to our User model with role
   */
  private convertFirebaseUser(firebaseUser: FirebaseUser): UserWithRole {
    const email = firebaseUser.email || '';
    const role = this.determineUserRole(email);
    const domain = this.extractDomain(email);

    return {
      id: firebaseUser.uid,
      name: firebaseUser.displayName || '',
      email: email,
      company: '', // Can be set later or pulled from custom claims
      photoURL: firebaseUser.photoURL,
      emailVerified: firebaseUser.emailVerified,
      role: role,
      domain: domain
    };
  }

  /**
   * Check if user is authorized for a specific application
   */
  isAuthorizedForApp(email: string, app: AppName): boolean {
    const normalizedEmail = email.toLowerCase();
    const domain = this.extractDomain(normalizedEmail);

    // Special case for Made.bb: check specific emails
    if (app === 'madebb') {
      if (this.madebbAuthorizedEmails.includes(normalizedEmail)) {
        return true;
      }
    }

    // Special case for Offshore Sails: check specific emails (OS-INV-04)
    if (app === 'offshore-sails') {
      if (this.offshoreAuthorizedEmails.includes(normalizedEmail)) {
        return true;
      }
    }

    // Check if user's domain is in the authorized domains list for this app
    return this.authorizedDomains[app].includes(domain);
  }

  /**
   * Validate authorization and throw error if not authorized
   */
  private validateAuthorization(email: string, app: AppName): void {
    if (!this.isAuthorizedForApp(email, app)) {
      throw new Error('Account not authorized');
    }
  }

  /**
   * Sign in with Google OAuth
   */
  async signInWithGoogle(app?: AppName): Promise<UserWithRole> {
    try {
      const result = await signInWithPopup(auth, googleProvider);
      const user = this.convertFirebaseUser(result.user);

      // Validate authorization for the app if specified
      if (app) {
        this.validateAuthorization(user.email, app);
      }

      // For admin access, check if email is in admin list
      if (this.isAdminEmail(user.email)) {
        // Could set custom claims here or handle admin status
        console.log('Admin user detected:', user.email);
      }

      return user;
    } catch (error: any) {
      // Sign out if authorization failed
      if (error.message === 'Account not authorized') {
        await signOut(auth);
      }
      console.error('Google sign-in error:', error);
      throw new Error(error.message || 'Failed to sign in with Google');
    }
  }

  /**
   * Sign in with email and password
   */
  async signInWithEmail(email: string, password: string, app?: AppName): Promise<UserWithRole> {
    try {
      // Validate authorization before attempting sign-in if app is specified
      if (app) {
        this.validateAuthorization(email, app);
      }

      const result = await signInWithEmailAndPassword(auth, email, password);
      return this.convertFirebaseUser(result.user);
    } catch (error: any) {
      console.error('Email sign-in error:', error);
      throw new Error(error.message || 'Failed to sign in with email');
    }
  }

  /**
   * Create account with email and password
   */
  async createAccount(email: string, password: string, displayName: string): Promise<UserWithRole> {
    try {
      const result = await createUserWithEmailAndPassword(auth, email, password);
      
      // Update profile with display name
      await updateProfile(result.user, {
        displayName: displayName
      });
      
      return this.convertFirebaseUser(result.user);
    } catch (error: any) {
      console.error('Account creation error:', error);
      throw new Error(error.message || 'Failed to create account');
    }
  }

  /**
   * Sign out current user
   */
  async signOut(): Promise<void> {
    try {
      await signOut(auth);
    } catch (error: any) {
      console.error('Sign out error:', error);
      throw new Error(error.message || 'Failed to sign out');
    }
  }

  /**
   * Get current user
   */
  getCurrentUser(): UserWithRole | null {
    const firebaseUser = auth.currentUser;
    return firebaseUser ? this.convertFirebaseUser(firebaseUser) : null;
  }

  /**
   * Check if current user is admin
   */
  isAdmin = (): boolean => {
    const user = this.getCurrentUser();
    return user?.email ? this.isAdminEmail(user.email) : false;
  }

  /**
   * Subscribe to authentication state changes
   */
  onAuthStateChange(callback: (user: UserWithRole | null) => void): () => void {
    this.authStateListeners.push(callback);
    
    // Return unsubscribe function
    return () => {
      this.authStateListeners = this.authStateListeners.filter(listener => listener !== callback);
    };
  }

  /**
   * Notify all listeners of auth state changes
   */
  private notifyListeners(user: UserWithRole | null) {
    this.authStateListeners.forEach(listener => listener(user));
  }

  /**
   * Check if user email is verified
   */
  isEmailVerified(): boolean {
    return auth.currentUser?.emailVerified || false;
  }

  /**
   * Send email verification
   */
  async sendEmailVerification(): Promise<void> {
    const user = auth.currentUser;
    if (!user) {
      throw new Error('No user is currently signed in');
    }

    try {
      const { sendEmailVerification } = await import('firebase/auth');
      await sendEmailVerification(user);
    } catch (error: any) {
      console.error('Email verification error:', error);
      throw new Error(error.message || 'Failed to send verification email');
    }
  }

  /**
   * Get Firebase ID token for API authentication
   */
  async getIdToken(forceRefresh = false): Promise<string | null> {
    const user = auth.currentUser;
    if (!user) {
      return null;
    }

    try {
      const token = await user.getIdToken(forceRefresh);
      return token;
    } catch (error: any) {
      console.error('Failed to get ID token:', error);
      throw new Error('Failed to get authentication token');
    }
  }

  /**
   * Get current Firebase user (raw)
   */
  getCurrentFirebaseUser(): FirebaseUser | null {
    return auth.currentUser;
  }

  /**
   * OS-INV-04: Read tenant memberships from the Firebase ID token custom claims.
   *
   * Backend sets a `tenants` custom claim (string or string[]) identifying which
   * tenant apps a staff user may access (e.g. ['offshore']). Returns a normalized
   * string[]; returns [] when signed out or when no tenant claim is present.
   * This is additive and does not affect any existing Made.bb auth path.
   */
  async getTenantClaims(): Promise<string[]> {
    const user = auth.currentUser;
    if (!user) return [];
    try {
      const result = await user.getIdTokenResult();
      const raw = (result.claims as Record<string, unknown>).tenants;
      if (Array.isArray(raw)) {
        return raw.filter((t): t is string => typeof t === 'string');
      }
      if (typeof raw === 'string' && raw.length > 0) {
        return [raw];
      }
      return [];
    } catch (error) {
      console.error('Failed to read tenant claims:', error);
      return [];
    }
  }

  /**
   * Get current user role
   */
  getCurrentUserRole(): UserRole {
    const user = this.getCurrentUser();
    return user?.role || 'customer';
  }

  /**
   * Check if current user has superadmin privileges
   */
  isSuperAdmin(): boolean {
    return this.getCurrentUserRole() === 'superadmin';
  }

  /**
   * Check if current user has admin privileges (admin or superadmin)
   */
  isAdminOrAbove(): boolean {
    const role = this.getCurrentUserRole();
    return role === 'superadmin' || role === 'admin';
  }

  /**
   * Check if current user has API customer privileges or above
   */
  isApiCustomerOrAbove(): boolean {
    const role = this.getCurrentUserRole();
    return role === 'superadmin' || role === 'admin' || role === 'api_customer';
  }

  /**
   * Check if current user can access admin panel
   */
  canAccessAdminPanel(): boolean {
    const role = this.getCurrentUserRole();
    return role === 'superadmin' || role === 'admin' || role === 'api_customer';
  }

  /**
   * Check if current user can access field mapping
   */
  canAccessFieldMapping(): boolean {
    return this.getCurrentUserRole() === 'superadmin';
  }

  /**
   * Check if current user can access API keys
   */
  canAccessApiKeys(): boolean {
    const role = this.getCurrentUserRole();
    return role === 'superadmin' || role === 'api_customer';
  }

  /**
   * Check if current user should see domain-filtered data
   */
  shouldFilterByDomain(): boolean {
    return this.getCurrentUserRole() === 'api_customer';
  }

  /**
   * Get current user domain for filtering
   */
  getCurrentUserDomain(): string {
    const user = this.getCurrentUser();
    return user?.domain || '';
  }
}

export const authService = new AuthService();
export default authService;